import { AgGridReact } from 'ag-grid-react';
import { NJButton, NJIconButton } from '@engie-group/fluid-design-system-react';
import type { ColDef } from 'ag-grid-community';
import PrototypeStudioLayout from '@shared/PrototypeStudioLayout';
import { FLUID_TYPE_LABEL, SIZE2MIX_AG_GRID_LOCALE, Size2MixAppShell, Size2MixNetworkIcon, Size2MixPageHeader, useSize2MixRefNavigation } from '@shared/size2mix-shell';
import { useNetworks } from './hooks';
import type { NetworkListItem } from './types';

export const meta = {
    title: 'Référence — Liste des réseaux',
    description: 'Page de référence « Réseaux » de Nemo : Size2MixAppShell titre + bouton plein, AG Grid filtrable (filtres de colonne) paginée avec raccourcis par réseau.',
    tags: ['reference', 'network', 'list'],
    author: 'dv5886',
    status: 'review' as const,
    category: 'hors-reseau/network-list',
    createdAt: '2026-10-10',
};

export default function RefNetworkList() {
    const nav = useSize2MixRefNavigation();
    const { data } = useNetworks();
    const rows = data?.items ?? [];

    const open = (row: NetworkListItem, page: 'general' | 'topology' | 'production' | 'plannings' | 'optimisations') => {
        if (row.eid === '300145213') nav.goTo(page);
    };

    const columns: ColDef<NetworkListItem>[] = [
        {
            headerName: 'Type',
            field: 'fluidType',
            maxWidth: 110,
            valueGetter: (p) => (p.data ? FLUID_TYPE_LABEL[p.data.fluidType] : ''),
            cellRenderer: (p: { data: NetworkListItem }) => (
                <span style={{ display: 'inline-flex', height: '100%', alignItems: 'center', gap: 8 }}>
                    <Size2MixNetworkIcon fluidType={p.data.fluidType} size={18} />
                    {FLUID_TYPE_LABEL[p.data.fluidType]}
                </span>
            ),
        },
        {
            headerName: 'Nom du réseau',
            field: 'name',
            flex: 3,
            cellRenderer: (p: { data: NetworkListItem }) => (
                <span style={{ cursor: 'pointer' }} onClick={() => open(p.data, 'general')}>
                    {p.data.name}
                </span>
            ),
        },
        { headerName: 'Date de création', field: 'createdAt', sort: 'desc' },
        { headerName: 'Créé par', field: 'createdBy' },
        {
            headerName: 'Actions',
            sortable: false,
            filter: false,
            minWidth: 200,
            cellRenderer: (p: { data: NetworkListItem }) => (
                <span style={{ display: 'inline-flex', gap: 2 }}>
                    <NJIconButton icon="map" label="Topologie" variant="tertiary" scale="sm" onClick={() => open(p.data, 'topology')} />
                    <NJIconButton icon="factory" label="Production" variant="tertiary" scale="sm" onClick={() => open(p.data, 'production')} />
                    <NJIconButton icon="insert_invitation" label="Plannings" variant="tertiary" scale="sm" onClick={() => open(p.data, 'plannings')} />
                    <NJIconButton icon="stacked_bar_chart" label="Optimisations" variant="tertiary" scale="sm" onClick={() => open(p.data, 'optimisations')} />
                </span>
            ),
        },
    ];

    return (
        <PrototypeStudioLayout title={meta.title}>
            <Size2MixAppShell
                activePrimary="networks"
                onPrimaryNavigate={nav.onPrimaryNavigate}
            >
                <Size2MixPageHeader
                    title={`Liste des réseaux (${data?.total ?? '…'})`}
                    actions={<NJButton label="Nouveau réseau" />}
                />
                <div className="ag-theme-quartz" style={{ marginTop: 16 }}>
                    <AgGridReact<NetworkListItem>
                        rowData={rows}
                        columnDefs={columns}
                        defaultColDef={{ flex: 1, sortable: true, filter: true, resizable: true, suppressMovable: true }}
                        domLayout="autoHeight"
                        localeText={SIZE2MIX_AG_GRID_LOCALE}
                        pagination
                        paginationPageSize={50}
                        paginationPageSizeSelector={[15, 50, 100]}
                    />
                </div>
            </Size2MixAppShell>
        </PrototypeStudioLayout>
    );
}
